import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import lodashGet from "lodash/get";

import StringTranslate from '../../assets/i18n/stringLanguage.jsx';
import stringHelper from '../../utils/stringHelper.js';

/* React components */
import {
  Dialog, IconButton, List, DialogContentText, DialogContent,
  DialogTitle, DialogActions, Button, TextField, InputAdornment,
  Snackbar, Grid,
  useMediaQuery
} from "@mui/material";

/* Own components */
import ClientRow from "./ClientRow";

/* Redux */
import { ActionClosePorfolio, ActionSetClientDataError } from "../../redux/actions/contextApp";
import { ActionDisconnect } from "../../redux/actions/connection.js";

/* Icons */
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { useTheme } from "@mui/styles";

/**
 * Portfolio en mode fonctionnel
 * @param {Object} props
 * @returns {JSX.Element}
 */
const Portfolio = () => {
  const dispatch = useDispatch();

  const isPortfolioOpen = useSelector(state => lodashGet(state, 'contextAppData.isPortfolioOpen', false));
  const portfolioClients = useSelector(state => lodashGet(state, 'clientUserData.portfolioClients', []));
  const selectedClientId = useSelector(state => lodashGet(state, 'contextAppData.selectedClientId', null));
  const clientLoading = useSelector(state => lodashGet(state, 'contextAppData.clientLoading', false));
  const clientError = useSelector(state => lodashGet(state, 'contextAppData.clientError', null));

  const [searchFilter, setSearchFilter] = useState('');
  const [currentPortFolioClients, setCurrentPortFolioClients] = useState([]);

  const theme = useTheme();
  const onlySmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  /**
   * Ferme la fenetre seulement si un client a été choisi
   */
  const handleClose = () => {
    if (selectedClientId > 0) {
      dispatch(ActionClosePorfolio());
    }
  };

  /**
   * Gère la déconnexion de l'utilisateur
   */
  const handleDisconnect = () => {
    dispatch(ActionDisconnect());
  };

  /**
   * Ferme le snackbar, clear l'erreur
   */
  const handleCloseSnackBar = () => {
    if (clientError) {
      dispatch(ActionSetClientDataError(null));
    }
  };

  /**
   * Fonction correspondant à la zone de recherche
   */
  const requestSearch = (searchValue) => {
    setSearchFilter(searchValue);

    try {
      const searchRegex = new RegExp(stringHelper.escapeRegExp(searchValue), 'i');
      const filteredRows = portfolioClients.filter((row) => {
        return Object.keys(row).some((field) => {
          let textValue = "" + row[field];
          return searchRegex.test(textValue.toString());
        });
      });
      setCurrentPortFolioClients(filteredRows);
    } catch (err) {
      // Peut arriver si on saisit un truc du genre 'EARL++' !
    }
  };

  return (
    <Dialog
      open={isPortfolioOpen}
      onClose={handleClose}
      scroll={'paper'}
      maxWidth={'md'}
    >
      <DialogTitle>{StringTranslate.myFarmers}</DialogTitle>
      <IconButton
        disabled={!selectedClientId || clientLoading}
        aria-label="close"
        onClick={handleClose}
        sx={(theme) => ({
          position: 'absolute',
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent
        dividers={true}
        sx={{
          minWidth: { xs: 250, sm: 400, md: 600, lg: 800 },
        }}
      >
        <Grid container direction="column">

          <Grid item xs={12} sx={{ mb: 3 }}>
            <Grid container spacing={1} sx={{ alignItems: "center" }}>
              <Grid item xs={8}>
                {/* Zone de recherche */}
                {portfolioClients.length > 1 &&
                  <TextField
                    value={searchFilter}
                    onChange={(event) => requestSearch(event.target.value)}
                    placeholder={StringTranslate.toolbarsearch}
                    fullWidth
                    variant="standard"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>),
                      endAdornment: (
                        <IconButton
                          title="Clear"
                          aria-label="Clear"
                          size="small"
                          style={{ visibility: searchFilter ? 'visible' : 'hidden' }}
                          onClick={() => requestSearch('')}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      ),
                    }}
                  />
                }
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <DialogContentText sx={{ pl: 2 }}>{StringTranslate.SelectFarmerToStart}</DialogContentText>

            {/* Liste clients */}
            {portfolioClients.length > 0 ?
              <List dense={true}>
                {(searchFilter !== "" ? currentPortFolioClients : portfolioClients).map((client, index) => (
                  <ClientRow key={index} clientInfos={client} maxLength={onlySmallScreen ? 15 : 30} />
                ))}
              </List> :
              <>
                {/* Aucun client */}
                <DialogContentText>{StringTranslate.noFarmers}</DialogContentText>
              </>
            }
          </Grid>

        </Grid>

      </DialogContent>
      <DialogActions
        sx={{
          display: 'flex',
          justifyContent: 'space-between'
        }}
      >
        <Button onClick={handleDisconnect} color={'error'}>
          {StringTranslate.disconnect}
        </Button>
        <Button onClick={handleClose} disabled={!selectedClientId}>
          {StringTranslate.close}
        </Button>
      </DialogActions>
      <Snackbar
        open={clientError !== null}
        autoHideDuration={6000}
        message={clientError}
        onClose={handleCloseSnackBar}
        action={
          <IconButton
            onClick={handleCloseSnackBar}
          >
            <CloseIcon />
          </IconButton>
        }
      />
    </Dialog>
  );
};

export default Portfolio;
